import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Flame, Coins, BookOpen, Zap, PlayCircle,
  ChevronRight, Clock, Target, FileText,
  Users, Calendar, Lock, Crown, TrendingUp, Menu, X, BarChart3, Info
} from 'lucide-react';
import { hasFeatureAccess } from '../utils/subscriptionFeatures';
import DailyReminderModal from '../components/DailyReminderModal';
import ScoreContractModal from '../components/ScoreContractModal';

const subjectIcons = { 
  Math: '📐', 
  Science: '🔬', 
  English: '📖', 
  'Social Science': '🌍', 
  Hindi: '📚', 
  EVS: '🌿', 
  Physics: '⚛️', 
  Chemistry: '🧪', 
  Biology: '🧬' 
};

const subjectColors = {
  Math: 'from-amber-500/20 to-amber-600/10 border-amber-500/30',
  Science: 'from-emerald-500/20 to-emerald-600/10 border-emerald-500/30',
  English: 'from-blue-500/20 to-blue-600/10 border-blue-500/30',
  'Social Science': 'from-cyan-500/20 to-cyan-600/10 border-cyan-500/30',
  Hindi: 'from-orange-500/20 to-orange-600/10 border-orange-500/30',
  Physics: 'from-violet-500/20 to-violet-600/10 border-violet-500/30',
  Chemistry: 'from-pink-500/20 to-pink-600/10 border-pink-500/30',
  Biology: 'from-green-500/20 to-green-600/10 border-green-500/30',
};

export default function StudentDashboard({ onNavigate }) {
  const { api, user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReminder, setShowReminder] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [primaryGoal, setPrimaryGoal] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [showMoreActions, setShowMoreActions] = useState(false);
  const [showDailyTooltip, setShowDailyTooltip] = useState(false);
  const [subjectChapters, setSubjectChapters] = useState({});
  
  const subscription = user?.subscription || 'free';

  const fetchDashboard = useCallback(async () => {
    try {
      const [dashRes, examsRes, readinessRes] = await Promise.all([
        api.get('/student/dashboard'),
        api.get('/exams/subject-wise'),
        api.get('/learning/exam-readiness').catch(() => ({ data: null })),
      ]);
      
      setDashboard(dashRes.data);
      setReadiness(readinessRes.data);
      
      // Find primary goal (nearest exam)
      if (examsRes.data && examsRes.data.length > 0) {
        const sortedExams = examsRes.data.sort((a, b) => 
          new Date(a.exam_date) - new Date(b.exam_date)
        );
        const upcomingExam = sortedExams.find(e => new Date(e.exam_date) > new Date());
        setPrimaryGoal(upcomingExam || null);
      }
      
      // Fetch chapter counts per subject
      const subjects = ['Math', 'Science', 'English', 'Social Science', 'Hindi'];
      const chapterCounts = {};
      for (const subject of subjects) {
        try {
          const res = await api.get(`/chapters?subject=${subject}&class_name=${user?.class_name || 'Class 10'}`);
          chapterCounts[subject] = (res.data || []).length;
        } catch (e) {
          chapterCounts[subject] = 0;
        }
      }
      setSubjectChapters(chapterCounts);
      
      // Check onboarding status
      const onboardingRes = await api.get('/user/onboarding-status');
      if (onboardingRes.data?.show_onboarding) {
        setTimeout(() => setShowOnboarding(true), 1500);
      }
      
      // Check first-use tooltip
      const tooltipsRes = await api.get('/user/tooltips-status').catch(() => ({ data: { tooltips_seen: {} } }));
      if (!tooltipsRes.data?.tooltips_seen?.daily_practice) {
        setTimeout(() => setShowDailyTooltip(true), 2000);
      }
      
    } catch (err) {
      console.error('Dashboard fetch failed:', err);
    }
    setLoading(false);
  }, [api, user?.class_name]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const handleCloseReminder = () => {
    setShowReminder(false);
    localStorage.setItem('studyclaw_reminder_dismiss', new Date().getTime().toString());
  };

  const dismissDailyTooltip = async () => {
    setShowDailyTooltip(false);
    try {
      await api.post('/user/tooltip-seen?tooltip_id=daily_practice');
    } catch (e) {
      console.debug('Tooltip dismiss failed');
    }
  };

  // Calculate days until exam
  const daysUntilExam = primaryGoal 
    ? Math.max(0, Math.ceil((new Date(primaryGoal.exam_date) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  // Get overall readiness score
  const overallReadiness = readiness?.overall_score || dashboard?.readiness_score || 0;
  const targetScore = primaryGoal?.target_score || 75;

  // Sessions needed calculation
  const sessionsNeeded = Math.max(0, Math.ceil((targetScore - overallReadiness) / 5));

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 rounded-xl bg-violet-600 flex items-center justify-center mx-auto mb-3 animate-pulse">
            <Zap className="w-5 h-5 text-white" />
          </div>
          <div className="w-8 h-8 border-2 border-violet-500/30 border-t-violet-500 rounded-full animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 pb-24" data-testid="student-dashboard">
      {/* Top Bar */}
      <div className="sticky top-0 z-40 bg-slate-950/90 backdrop-blur-lg border-b border-slate-800/50 px-4 py-3">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          <div className="flex items-center gap-4">
            {/* Streak */}
            <div className="flex items-center gap-1.5 bg-orange-500/10 px-2.5 py-1.5 rounded-lg">
              <Flame className="w-4 h-4 text-orange-400" />
              <span className="text-sm font-bold text-white">{dashboard?.streak || 0}</span>
              <span className="text-xs text-slate-400">day streak</span>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* XP */}
            <div className="flex items-center gap-1 bg-violet-500/10 px-2.5 py-1.5 rounded-lg">
              <Zap className="w-4 h-4 text-violet-400" />
              <span className="text-sm font-bold text-white">{dashboard?.xp || 0}</span>
              <span className="text-xs text-slate-400">XP</span>
            </div>
            
            {/* Coins */}
            <div className="flex items-center gap-1 bg-amber-500/10 px-2.5 py-1.5 rounded-lg">
              <Coins className="w-4 h-4 text-amber-400" />
              <span className="text-sm font-bold text-white">{dashboard?.coins || 0}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 pt-5 max-w-lg mx-auto">
        {/* Welcome Section */}
        <div className="mb-6">
          <p className="text-slate-400 text-sm">Welcome back</p>
          <h1 className="text-2xl font-bold text-white">{user?.name || 'Student'}</h1>
        </div>

        {/* Daily Practice Card */}
        <div className="relative mb-6">
          <motion.button
            onClick={() => onNavigate('daily-loop')}
            className="w-full bg-gradient-to-r from-violet-600 to-violet-500 rounded-2xl p-5 text-left shadow-xl shadow-violet-500/20 hover:shadow-violet-500/30 transition-all group"
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            data-testid="daily-practice-card"
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <PlayCircle className="w-5 h-5 text-white/80" />
                  <span className="text-white/80 text-sm font-medium">Daily Practice</span>
                </div>
                <h2 className="text-xl font-bold text-white mb-1">5-Minute Session</h2>
                <p className="text-white/70 text-sm">Build your streak — practice now</p>
              </div>
              <ChevronRight className="w-6 h-6 text-white/60 group-hover:translate-x-1 transition-transform" />
            </div>
          </motion.button>

          {/* First-use Tooltip */}
          <AnimatePresence>
            {showDailyTooltip && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="absolute top-full left-4 right-4 mt-2 bg-slate-800 border border-slate-700 rounded-xl p-4 shadow-xl z-10"
              >
                <button onClick={dismissDailyTooltip} className="absolute top-2 right-2 text-slate-500 hover:text-white">
                  <X className="w-4 h-4" />
                </button>
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-violet-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-white mb-1">This 5-minute session replaces 30 minutes of cramming.</p>
                    <p className="text-xs text-slate-400">Your streak resets if you skip a day — don&apos;t break the chain.</p>
                    <button onClick={dismissDailyTooltip} className="text-violet-400 text-sm font-medium mt-2 hover:text-violet-300">
                      Got it
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Exam Readiness Card */}
        {primaryGoal && (
          <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5 mb-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">UT1</span>
                <h3 className="text-lg font-bold text-white">{primaryGoal.subject || 'All Subjects'}</h3>
              </div>
              <div className="text-right">
                <span className="text-3xl font-bold text-white">{daysUntilExam}</span>
                <p className="text-xs text-slate-400">days left</p>
              </div>
            </div>

            {/* Readiness Progress */}
            <div className="mb-3">
              <div className="flex justify-between items-center mb-1.5">
                <span className="text-sm text-slate-400">Readiness</span>
                <span className="text-sm font-semibold">
                  <span className={overallReadiness >= targetScore ? 'text-emerald-400' : 'text-amber-400'}>
                    {Math.round(overallReadiness)}%
                  </span>
                  <span className="text-slate-500"> / {targetScore}%</span>
                </span>
              </div>
              <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all duration-500 ${
                    overallReadiness >= targetScore ? 'bg-emerald-500' : 'bg-amber-500'
                  }`}
                  style={{ width: `${Math.min(100, (overallReadiness / targetScore) * 100)}%` }}
                />
              </div>
            </div>

            <div className="flex items-center gap-1.5 text-sm">
              <Clock className="w-4 h-4 text-slate-500" />
              <span className="text-slate-400">
                You need <span className="text-white font-medium">{sessionsNeeded} more sessions</span> to stay on track
              </span>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          {[
            { icon: BookOpen, label: 'Chapters', page: 'chapters', color: 'violet' },
            { icon: FileText, label: 'Mock Exam', page: 'mock-exam', color: 'cyan' },
            { icon: Calendar, label: 'Calendar', page: 'exam-calendar', color: 'amber' },
            { icon: BarChart3, label: 'Readiness', page: 'readiness', color: 'emerald' },
          ].map((action) => (
            <motion.button
              key={action.page}
              onClick={() => onNavigate(action.page)}
              className="flex flex-col items-center gap-2 p-3 bg-slate-900/50 border border-slate-800 rounded-xl hover:border-slate-700 transition-all"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid={`quick-action-${action.page}`}
            >
              <div className={`w-10 h-10 rounded-xl bg-${action.color}-500/20 flex items-center justify-center`}>
                <action.icon className={`w-5 h-5 text-${action.color}-400`} />
              </div>
              <span className="text-xs text-slate-400 font-medium">{action.label}</span>
            </motion.button>
          ))}
        </div>

        {/* More Actions (Collapsible) */}
        <button
          onClick={() => setShowMoreActions(!showMoreActions)}
          className="w-full flex items-center justify-center gap-2 py-2 text-sm text-slate-500 hover:text-slate-300 transition-all mb-4"
        >
          <Menu className="w-4 h-4" />
          {showMoreActions ? 'Less actions' : 'More actions'}
        </button>

        <AnimatePresence>
          {showMoreActions && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="grid grid-cols-4 gap-3 mb-6"
            >
              {[
                { icon: Users, label: 'Groups', page: 'group-study', locked: !hasFeatureAccess(subscription, 'group_study') },
                { icon: Target, label: 'Set Goal', page: 'set-goal', onClick: () => setShowOnboarding(true) },
                { icon: TrendingUp, label: 'Progress', page: 'progress' },
                { icon: Crown, label: 'Pro', page: 'subscription' },
              ].map((action) => (
                <motion.button
                  key={action.page}
                  onClick={action.onClick || (() => onNavigate(action.page))}
                  className="relative flex flex-col items-center gap-2 p-3 bg-slate-900/50 border border-slate-800 rounded-xl hover:border-slate-700 transition-all"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {action.locked && (
                    <div className="absolute top-1 right-1">
                      <Lock className="w-3 h-3 text-slate-600" />
                    </div>
                  )}
                  <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center">
                    <action.icon className="w-5 h-5 text-slate-400" />
                  </div>
                  <span className="text-xs text-slate-500 font-medium">{action.label}</span>
                </motion.button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Subjects Section */}
        <div className="mb-6">
          <h2 className="text-lg font-bold text-white mb-4">Subjects</h2>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(subjectChapters).map(([subject, count]) => (
              <motion.button
                key={subject}
                onClick={() => onNavigate('chapters', { subject })}
                className={`bg-gradient-to-br ${subjectColors[subject] || 'from-slate-800 to-slate-900 border-slate-700'} border rounded-xl p-4 text-left hover:scale-[1.02] transition-all`}
                whileTap={{ scale: 0.98 }}
                data-testid={`subject-card-${subject.toLowerCase().replace(' ', '-')}`}
              >
                <span className="text-2xl mb-2 block">{subjectIcons[subject] || '📘'}</span>
                <h3 className="font-semibold text-white text-sm">{subject}</h3>
                <p className="text-xs text-slate-400">{count} chapters</p>
              </motion.button>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        {dashboard?.recent_activity && dashboard.recent_activity.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-bold text-white mb-4">Recent Activity</h2>
            <div className="space-y-2">
              {dashboard.recent_activity.slice(0, 3).map((activity) => (
                <div key={activity.id || `${activity.type}-${activity.title}`} className="bg-slate-900/50 border border-slate-800 rounded-xl p-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-violet-500/20 flex items-center justify-center">
                    {activity.type === 'quiz' && <Zap className="w-4 h-4 text-violet-400" />}
                    {activity.type === 'essence' && <BookOpen className="w-4 h-4 text-amber-400" />}
                    {activity.type === 'session' && <PlayCircle className="w-4 h-4 text-emerald-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{activity.title}</p>
                    <p className="text-xs text-slate-500">{activity.time_ago || 'Recently'}</p>
                  </div>
                  {activity.score && (
                    <span className="text-sm font-semibold text-violet-400">{activity.score}%</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-slate-950/95 backdrop-blur-lg border-t border-slate-800 px-4 py-2 z-50">
        <div className="max-w-lg mx-auto flex justify-around">
          {[
            { icon: '🏠', label: 'Home', page: 'dashboard', active: true },
            { icon: '📚', label: 'Chapters', page: 'chapters' },
            { icon: '📝', label: 'Exams', page: 'exam-calendar' },
            { icon: '👥', label: 'Groups', page: 'group-study' },
            { icon: '👤', label: 'Profile', page: 'profile' },
          ].map((item) => (
            <button
              key={item.page}
              onClick={() => onNavigate(item.page)}
              className={`flex flex-col items-center gap-1 px-3 py-1 rounded-lg transition-all ${
                item.active ? 'text-violet-400' : 'text-slate-500 hover:text-slate-300'
              }`}
              data-testid={`nav-${item.page}`}
            >
              <span className="text-lg">{item.icon}</span>
              <span className="text-[10px] font-medium">{item.label}</span>
              {item.active && <div className="w-1 h-1 rounded-full bg-violet-500 mt-0.5" />}
            </button>
          ))}
        </div>
      </nav>

      {/* Modals */}
      <DailyReminderModal 
        isOpen={showReminder} 
        onClose={handleCloseReminder}
        onStartSession={() => { handleCloseReminder(); onNavigate('daily-loop'); }}
      />

      <ScoreContractModal
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        onNavigate={onNavigate}
      />
    </div>
  );
}
